import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPut, apiPost, adminCompanyPath } from '../../lib/api-client';
import { useCompany } from '../../contexts/CompanyContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

interface ApiConfigData {
  id: string;
  apiUrl: string;
  username: string;
  passwordSet: boolean;
  lastSyncAt: string | null;
}

export function ApiConfig() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { activeCompanyId } = useCompany();

  const [apiUrl, setApiUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [testResult, setTestResult] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['admin', 'api-config', activeCompanyId],
    queryFn: () => apiGet<ApiConfigData | null>(adminCompanyPath('/api-config', activeCompanyId!)),
    enabled: !!activeCompanyId,
  });

  useEffect(() => {
    if (data?.ok && data.data) {
      setApiUrl(data.data.apiUrl);
      setUsername(data.data.username);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiPut(adminCompanyPath('/api-config', activeCompanyId!), { apiUrl, username, password }),
    onSuccess: (res) => {
      if (res.ok) {
        setMessage(t('common.saved'));
        setPassword('');
        queryClient.invalidateQueries({ queryKey: ['admin', 'api-config'] });
      } else {
        setMessage(res.error.message);
      }
    },
  });

  const testMutation = useMutation({
    mutationFn: () =>
      apiPost<{ connected: boolean; message: string }>(
        adminCompanyPath('/api-config/test-connection', activeCompanyId!),
      ),
    onSuccess: (res) => {
      if (res.ok) {
        setTestResult(res.data.message);
      } else {
        setTestResult(res.error.message);
      }
    },
  });

  if (!activeCompanyId) {
    return (
      <div className="space-y-6">
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          {t('company.noCompanySelected')}
        </div>
      </div>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    saveMutation.mutate();
  };

  return (
    <div className="space-y-6">
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>{t('admin.coreApiCredentials')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {message && (
              <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                {message}
              </div>
            )}
            <Input
              label={t('admin.apiUrl')}
              type="url"
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
              placeholder="https://company-api.pgway.work"
              required
            />
            <Input
              label={t('auth.username')}
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
            <Input
              label={t('auth.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={data?.ok && data.data?.passwordSet ? t('admin.passwordUnchanged') : ''}
              required={!(data?.ok && data.data?.passwordSet)}
            />
            <div className="flex gap-3">
              <Button type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? t('common.loading') : t('common.save')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => testMutation.mutate()}
                disabled={testMutation.isPending}
              >
                {testMutation.isPending ? t('common.loading') : t('admin.testConnection')}
              </Button>
            </div>
          </form>
          {testResult && (
            <div className="mt-4 rounded-md bg-gray-50 p-3 text-sm text-gray-700 dark:bg-gray-900 dark:text-gray-300">
              {testResult}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
